

import React, { useState } from "react"
import { useParams } from "react-router"
import useTitle from "@/hooks/useTitle"
import PulseLoader from "react-spinners/PulseLoader"

import {
  useVerifySchedulingPagePasscodeMutation,
  useGetPublicSchedulingSlotsQuery,
  useBookPublicSchedulingSlotMutation,
} from "@/features/userSchedulingPages/userSchedulingPagesApiSlice"

const EtPublicSlotBoard = () => {

  // First Created - 11 May 2026
  // Author - Atul Pandey
  // Purpose - Public SlotBoard page for cohort users to verify passcode, view available slots, and book a discussion slot.
  // Updated - 18 May 2026 - Added US Mountain timezone
  
  
  useTitle("Trinity SlotBoard")

  const { publicSlug } = useParams()

  const [isVerified, setIsVerified] = useState(false)
  const [passcode, setPasscode] = useState("")
  const [selectedSlotId, setSelectedSlotId] = useState("")
  const [bookedByName, setBookedByName] = useState("")
  const [bookedByEmail, setBookedByEmail] = useState("")
  const [bookedByTimezone, setBookedByTimezone] = useState("America/Los_Angeles")
  const [bookingNote, setBookingNote] = useState("")
  const [confirmationMessage, setConfirmationMessage] = useState("")

  const [
    verifySchedulingPagePasscode,
    {
      data: verifiedPageData,
      isLoading: isVerifying,
      isError: isVerifyError,
      error: verifyError,
    },
  ] = useVerifySchedulingPagePasscodeMutation()

  const {
    data: publicSlotsData,
    isLoading: isLoadingSlots,
    isError: isSlotsError,
    error: slotsError,
    refetch,
  } = useGetPublicSchedulingSlotsQuery(
    { publicSlug },
    { skip: !isVerified || !publicSlug }
  )

  const [
    bookPublicSchedulingSlot,
    {
      isLoading: isBooking,
      isError: isBookingError,
      error: bookingError,
    },
  ] = useBookPublicSchedulingSlotMutation()

  const handleVerifyPasscode = async e => {
    e.preventDefault()

    if (!publicSlug || !passcode) return

    try {
      const response = await verifySchedulingPagePasscode({
        publicSlug,
        passcode,
      }).unwrap()

      if (response?.success) {
        setIsVerified(true)

        if (response?.allowedTimezones?.length > 0) {
          setBookedByTimezone(response.allowedTimezones[0].timezone)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleBookSlot = async e => {
    e.preventDefault()

    if (!selectedSlotId || !bookedByName || !bookedByEmail) return

    try {
      const response = await bookPublicSchedulingSlot({
        publicSlug,
        slotId: selectedSlotId,
        bookedByName,
        bookedByEmail,
        bookedByTimezone,
        bookingNote,
      }).unwrap()

      setConfirmationMessage(response?.message || "Slot booked successfully.")
      setSelectedSlotId("")
      setBookedByName("")
      setBookedByEmail("")
      setBookingNote("")
      refetch()
    } catch (error) {
      console.error(error)
    }
  }

  const formatInTimezone = (dateValue, timezone) => {
    if (!dateValue || !timezone) return ""

    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(new Date(dateValue))
  }

  const allowedTimezones =
    verifiedPageData?.allowedTimezones ||
    publicSlotsData?.allowedTimezones ||
    [
      { label: "US Pacific", timezone: "America/Los_Angeles" },
      { label: "US Mountain", timezone: "America/Denver" }, // 18 May 2026
      { label: "US Central", timezone: "America/Chicago" },
      { label: "US Eastern", timezone: "America/New_York" },
      { label: "UK", timezone: "Europe/London" },
      { label: "India", timezone: "Asia/Kolkata" },
    ]

  const slots = publicSlotsData?.slots || []

  if (!isVerified) {
    return (
      <div className="min-h-screen bg-gray-100 flex justify-center items-center font-poppins">
        <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-md">
          <p className="text-2xl font-bold text-gray-900 m-1 p-1">
            Trinity SlotBoard
          </p>

          <p className="text-sm text-gray-600 m-1 p-1">
            Please enter the passcode shared with you to view available discussion slots.
          </p>

          {isVerifyError && (
            <p className="text-orange-700 text-xs m-1 p-1">
              {verifyError?.data?.message || "Unable to verify passcode."}
            </p>
          )}

          <form onSubmit={handleVerifyPasscode} className="m-1 p-1">
            <input
              type="text"
              value={passcode}
              onChange={e => setPasscode(e.target.value)}
              placeholder="Enter passcode"
              className="w-full rounded p-2 m-1 border border-gray-200 text-sm"
            />

            <button
              type="submit"
              disabled={isVerifying}
              className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold rounded px-4 py-2 m-1 disabled:bg-gray-400"
            >
              {isVerifying ? "Verifying..." : "Continue"}
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100 font-poppins p-4">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-md p-4">
        <div className="border-b border-gray-200 pb-3 mb-3">
          <p className="text-2xl font-bold text-gray-900">
            {publicSlotsData?.pageName || verifiedPageData?.pageName || "Trinity SlotBoard"}
          </p>

          {(publicSlotsData?.pageDescription || verifiedPageData?.pageDescription) && (
            <p className="text-sm text-gray-600 mt-1">
              {publicSlotsData?.pageDescription || verifiedPageData?.pageDescription}
            </p>
          )}
        </div>

        {confirmationMessage && (
          <div className="bg-green-50 border border-green-200 rounded p-3 mb-3">
            <p className="text-sm text-green-700">{confirmationMessage}</p>
          </div>
        )}

        {isLoadingSlots && <PulseLoader color={"#ea580c"} />}

        {isSlotsError && (
          <p className="text-orange-700 text-xs">
            {slotsError?.data?.message || "Unable to load slots."}
          </p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="bg-gray-100 rounded p-3">
            <div className="flex justify-between items-center mb-2">
              <p className="text-lg font-bold">Available Slots</p>
              <p className="text-xs font-bold text-orange-700 bg-white rounded px-2 py-1">
                {slots?.length || 0} available
              </p>
            </div>

            {slots?.length === 0 && !isLoadingSlots && (
              <p className="text-sm text-gray-600">
                No available slots are currently open.
              </p>
            )}

            <div className="space-y-2 lg:max-h-[520px] lg:overflow-y-auto pr-1">
              {slots.map((slot, index) => (
                <div
                  key={slot._id}
                  onClick={() => setSelectedSlotId(slot._id)}
                  className={`rounded p-3 cursor-pointer border ${
                    selectedSlotId === slot._id
                      ? "border-orange-600 bg-orange-50"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <p className="text-sm font-bold text-gray-900">
                    <span className="text-orange-700">{index + 1}.</span> {formatInTimezone(slot.slotStart, bookedByTimezone)} to{" "}
                    {formatInTimezone(slot.slotEnd, bookedByTimezone)}
                  </p>

                  <div className="mt-2">
                    {allowedTimezones.map((tzItem, index) => (
                      <p key={index} className="text-[10px] text-gray-600">
                        <span className="font-bold">{tzItem.label}:</span>{" "}
                        {formatInTimezone(slot.slotStart, tzItem.timezone)}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-100 rounded p-3">
            <p className="text-lg font-bold mb-2">Book Selected Slot</p>

            {isBookingError && (
              <p className="text-orange-700 text-xs m-1 p-1">
                {bookingError?.data?.message || "Unable to book this slot."}
              </p>
            )}

            <form onSubmit={handleBookSlot} className="text-xs">
              <select
                value={bookedByTimezone}
                onChange={e => setBookedByTimezone(e.target.value)}
                className="w-full rounded p-2 m-1"
              >
                {allowedTimezones.map((tzItem, index) => (
                  <option key={index} value={tzItem.timezone}>
                    {tzItem.label}
                  </option>
                ))}
              </select>

              <input
                type="text"
                value={bookedByName}
                onChange={e => setBookedByName(e.target.value)}
                placeholder="Your name"
                className="w-full rounded p-2 m-1"
              />

              <input
                type="email"
                value={bookedByEmail}
                onChange={e => setBookedByEmail(e.target.value)}
                placeholder="Your email"
                className="w-full rounded p-2 m-1"
              />

              <textarea
                value={bookingNote}
                onChange={e => setBookingNote(e.target.value)}
                placeholder="Optional note"
                className="w-full rounded p-2 m-1"
                rows={4}
              />

              <button
                type="submit"
                disabled={isBooking || !selectedSlotId}
                className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold rounded px-4 py-2 m-1 disabled:bg-gray-400"
              >
                {isBooking ? "Booking..." : "Book Slot"}
              </button>

              {!selectedSlotId && (
                <p className="text-[10px] text-gray-500 m-1">
                  Please select a slot before booking.
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EtPublicSlotBoard